import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import AnimatedSection from '../components/AnimatedSection';
import { siteInfo } from '../data/siteInfo';

const IconLocal = () => (
  <svg className="about-why-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z"
      fill="currentColor"
      opacity="0.9"
    />
  </svg>
);

const IconTransparent = () => (
  <svg className="about-why-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"
      fill="currentColor"
      opacity="0.9"
    />
  </svg>
);

const IconResults = () => (
  <svg className="about-why-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z" fill="currentColor" opacity="0.9" />
  </svg>
);

const IconPersonal = () => (
  <svg className="about-why-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.89 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
      fill="currentColor"
      opacity="0.9"
    />
  </svg>
);

const VALUE_ICONS = [IconLocal, IconTransparent, IconResults, IconPersonal];

const About = () => {
  const { companyName, brandName, foundedYear, legal, contact } = siteInfo;

  return (
    <>
      <SEO
        title={`About ${companyName} | Property Investment in Preston`}
        description={siteInfo.shortDescription}
      />

      <section className="about-page-hero">
        <div className="about-page-hero__bg" aria-hidden="true">
          <img src="https://picsum.photos/seed/about-hero-em/1920/600" alt="" className="about-page-hero__img" />
          <div className="about-page-hero__overlay" />
        </div>
        <div className="container about-page-hero__inner">
          <AnimatedSection variant="fadeUp">
            <p className="about-page-hero__eyebrow">{siteInfo.locationLabel}</p>
            <h1 className="about-page-hero__title">About {brandName}</h1>
            <p className="about-page-hero__lead about-page-hero__lead--premium">
              {siteInfo.tagline}
            </p>
          </AnimatedSection>
        </div>
      </section>

      <section className="about-intro-band" aria-label="Mission and Vision">
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '40px', textAlign: 'center' }}>
          <AnimatedSection variant="fadeUp">
            <h3 style={{ color: 'var(--gold)', fontFamily: 'var(--font-heading)', fontSize: '1.8rem', marginBottom: '12px' }}>Our Mission</h3>
            <p className="about-intro-band__text" style={{ fontSize: '1.1rem', lineHeight: 1.7, maxWidth: '100%' }}>{siteInfo.mission}</p>
          </AnimatedSection>
          <AnimatedSection variant="fadeUp" delay={0.15}>
            <h3 style={{ color: 'var(--gold)', fontFamily: 'var(--font-heading)', fontSize: '1.8rem', marginBottom: '12px' }}>Our Vision</h3>
            <p className="about-intro-band__text" style={{ fontSize: '1.1rem', lineHeight: 1.7, maxWidth: '100%' }}>{siteInfo.vision}</p>
          </AnimatedSection>
        </div>
      </section>

      <section className="about-story-section">
        <div className="container">
          <div className="about-story-grid">
            <AnimatedSection variant="fadeLeft">
              <div className="about-story-text">
                <p className="eyebrow-label">Based in Preston</p>
                <h2 className="section-heading gold-underline">Who We Are</h2>
                <div className="about-story-text__body">
                  <p>{siteInfo.longDescription}</p>
                  <p>{siteInfo.investorSupport || siteInfo.aboutExtra}</p>
                  <p>{siteInfo.belief}</p>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection variant="fadeRight">
              <div className="about-story-media">
                <img
                  src="https://picsum.photos/seed/our-story-em/600/700"
                  alt={`${brandName} property investment in Preston`}
                  className="about-story-media__img"
                  loading="lazy"
                />
                <div className="about-story-media__badge">
                  <div className="about-story-media__badge-num">{foundedYear}</div>
                  <div className="about-story-media__badge-label">Established</div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="about-values-section about-why-section" aria-labelledby="about-why-heading">
        <div className="container">
          <AnimatedSection variant="fadeUp" className="section-intro section-intro--center about-section-intro">
            <p className="eyebrow-label">How We Work</p>
            <h2 id="about-why-heading" className="section-heading gold-underline">
              Our Approach
            </h2>
          </AnimatedSection>
          <div className="about-values-grid">
            {siteInfo.values.map((item, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <AnimatedSection key={item.title} variant="scaleIn" delay={i * 0.1}>
                  <motion.div className="about-value-card about-value-card--why" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                    <div className="about-value-card__icon about-value-card__icon--svg" aria-hidden="true">
                      <Icon />
                    </div>
                    <h3 className="about-value-card__title">{item.title}</h3>
                    <p className="about-value-card__desc">{item.description}</p>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <section className="about-company-section" aria-labelledby="about-company-heading">
        <div className="container">
          <AnimatedSection variant="fadeUp">
            <div className="about-company-card">
              <h2 id="about-company-heading" className="about-company-card__title">
                Company Information
              </h2>
              <dl className="about-company-card__grid">
                <div className="about-company-card__row">
                  <dt>Company name</dt>
                  <dd>{companyName}</dd>
                </div>
                <div className="about-company-card__row">
                  <dt>Company number</dt>
                  <dd>{legal.companyNumber}</dd>
                </div>
                <div className="about-company-card__row">
                  <dt>Established</dt>
                  <dd>{foundedYear}</dd>
                </div>
                {contact.fullAddress && (
                  <div className="about-company-card__row">
                    <dt>Location</dt>
                    <dd>{contact.fullAddress}</dd>
                  </div>
                )}
                <div className="about-company-card__row">
                  <dt>Business type</dt>
                  <dd>{legal.companyType}</dd>
                </div>
                <div className="about-company-card__row about-company-card__row--full">
                  <dt>What we do</dt>
                  <dd>
                    Property investment company focused on acquiring, developing and managing property investments for the long term — including buying, selling and letting.
                  </dd>
                </div>
              </dl>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="about-areas-section">
        <div className="container">
          <AnimatedSection variant="fadeUp" className="section-intro section-intro--center about-section-intro">
            <h2 className="section-heading gold-underline">Our Focus</h2>
            <p className="section-subheading about-areas-section__sub">
              Practical support across the areas that matter most to property investors
            </p>
          </AnimatedSection>
          <AnimatedSection variant="fadeUp" delay={0.15}>
            <div className="about-areas-chips">
              {siteInfo.focusAreas.map((area) => (
                <span key={area} className="about-area-chip">
                  {area}
                </span>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="about-cta-section">
        <div className="container about-cta-section__inner">
          <AnimatedSection variant="fadeUp">
            <h2 className="about-cta-section__title">{siteInfo.closingLine}</h2>
            <p className="about-cta-section__text">
              Get in touch if you are building a portfolio, selling a property, or exploring a new investment opportunity.
            </p>
            <div className="about-cta-section__actions">
              <Link to="/services" className="btn-primary">
                Our Services
              </Link>
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default About;
